import { Phone, Mail, MapPin, Clock, Navigation } from "lucide-react";
import { BUSINESS, MAPS_EMBED, MAPS_DIRECTIONS } from "../data/site";
import { Reveal, Chapter } from "./Reveal";

const BTN =
  "group flex items-center justify-center gap-3 whitespace-nowrap px-7 py-4 text-xs font-bold uppercase tracking-widest transition-all duration-300";

export default function Contact() {
  return (
    <section id="contact" data-testid="contact-section" className="mx-auto max-w-7xl px-5 py-24 md:px-10 md:py-36">
      <Chapter number="04" title="Find Us" />
      <div className="mt-6 mb-14">
        <Reveal delay={0.05}>
          <h2 className="max-w-2xl font-serif text-4xl font-bold leading-tight tracking-tight md:text-6xl">
            Come say <span className="italic text-ember">g'day</span>
          </h2>
        </Reveal>
      </div>

      <div className="grid gap-14 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <Reveal>
            <div className="space-y-8">
              <div data-testid="contact-address" className="flex gap-4 transition-transform duration-300 hover:translate-x-1.5">
                <MapPin size={22} className="mt-1 shrink-0 text-ember" />
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.25em] text-charcoal/50">Address</p>
                  <p className="mt-1 text-base font-medium">{BUSINESS.address}</p>
                </div>
              </div>
              <div data-testid="contact-phone" className="flex gap-4 transition-transform duration-300 hover:translate-x-1.5">
                <Phone size={22} className="mt-1 shrink-0 text-ember" />
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.25em] text-charcoal/50">Phone</p>
                  <a href={BUSINESS.phoneHref} className="mt-1 block text-base font-medium transition-colors hover:text-ember">
                    {BUSINESS.phoneDisplay}
                  </a>
                </div>
              </div>
              <div data-testid="contact-email" className="flex gap-4 transition-transform duration-300 hover:translate-x-1.5">
                <Mail size={22} className="mt-1 shrink-0 text-ember" />
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.25em] text-charcoal/50">Email</p>
                  <a href={`mailto:${BUSINESS.email}`} className="mt-1 block break-all text-base font-medium transition-colors hover:text-ember">
                    {BUSINESS.email}
                  </a>
                </div>
              </div>
              <div data-testid="contact-hours" className="flex gap-4 transition-transform duration-300 hover:translate-x-1.5">
                <Clock size={22} className="mt-1 shrink-0 text-ember" />
                <div className="flex-1">
                  <p className="text-xs font-bold uppercase tracking-[0.25em] text-charcoal/50">Opening Hours</p>
                  <ul className="mt-2 space-y-1">
                    {BUSINESS.hours.map((h) => (
                      <li key={h.day} className="flex items-baseline text-sm">
                        <span className="w-24 font-semibold">{h.day}</span>
                        <span className="menu-leader" />
                        <span className={h.time === "Closed" ? "text-charcoal/40" : "text-charcoal/80"}>{h.time}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-12 flex flex-col gap-3 sm:flex-row">
              <a data-testid="call-now-button" href={BUSINESS.phoneHref} className={`${BTN} rounded-full bg-ember text-cream hover:scale-105 hover:bg-[#991B1B]`}>
                <Phone size={14} /> Call Now
              </a>
              <a data-testid="directions-button" href={MAPS_DIRECTIONS} target="_blank" rel="noopener noreferrer" className={`${BTN} rounded-full border border-charcoal/25 text-charcoal hover:border-ember hover:text-ember`}>
                <Navigation size={14} /> Get Directions
              </a>
              <a data-testid="email-button" href={`mailto:${BUSINESS.email}`} className={`${BTN} rounded-full border border-charcoal/25 text-charcoal hover:border-ember hover:text-ember`}>
                <Mail size={14} /> Email Us
              </a>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <Reveal delay={0.1} className="h-full">
            <div className="relative h-full min-h-[380px] overflow-hidden border border-charcoal/15">
              <iframe
                data-testid="google-map-embed"
                title="Croudace Pizza & Burger location map"
                src={MAPS_EMBED}
                className="absolute inset-0 h-full w-full"
                style={{ border: 0 }}
                loading="lazy"
                allowFullScreen
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
